import React from 'react';
import { Grid } from '@mui/material';
import MultiSelectInput from '@components/common/input/MultiSelectInput';
import {
  FormFieldType,
  LinkedGroupedMultiSelectGroupedItem,
  LinkedGroupedMultiSelectItem,
  SetState,
} from '@/type';
import { isArrayNotEmpty } from '@/util';

interface LinkedGroupedMultiSelectInputProps {
  name: string;
  items: LinkedGroupedMultiSelectItem[];
  groupedItems: LinkedGroupedMultiSelectGroupedItem[];
  itemLabel: string;
  groupLabel: string;
  selectedItemIds: string[];
  setSelectedItemIds: SetState<string[]>;
  selectedGroupIds: string[];
  setSelectedGroupIds: SetState<string[]>;
}

const LinkedGroupedMultiSelectInput = ({
  name,
  items,
  groupedItems,
  itemLabel,
  groupLabel,
  selectedItemIds,
  setSelectedItemIds,
  selectedGroupIds,
  setSelectedGroupIds,
}: LinkedGroupedMultiSelectInputProps) => {
  const handleItemChange = (itemIds: string[]) => {
    setSelectedItemIds(itemIds);

    const associatedGroupIds = groupedItems
      .filter(
        (group) =>
          isArrayNotEmpty(group.items) &&
          group.items.every((itemId) => itemIds.includes(itemId)),
      )
      .map(({ id }) => id);

    setSelectedGroupIds(associatedGroupIds);
  };

  const handleGroupChange = (groupIds: string[]) => {
    const prevSelectedGroups = groupedItems.filter((group) =>
      selectedGroupIds.includes(group.id),
    );

    setSelectedGroupIds(groupIds);

    const assignedItemIds = groupedItems
      .filter((group) => groupIds.includes(group.id))
      .flatMap((group) => group.items);

    const selectedGroupsNotContainsItemId = (itemId: string) =>
      prevSelectedGroups.findLastIndex((group) => group.items.includes(itemId)) === -1;

    const notAssignedItemIds = selectedItemIds.filter(selectedGroupsNotContainsItemId);

    setSelectedItemIds(Array.from(new Set([...assignedItemIds, ...notAssignedItemIds])));
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <MultiSelectInput
          field={{
            value: selectedItemIds,
            onChange: handleItemChange,
          }}
          multiSelectField={{
            name: name,
            type: FormFieldType.MultiSelect,
            label: itemLabel,
            options: items.map((item) => ({ id: item.id, label: item.name })),
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <MultiSelectInput
          field={{
            value: selectedGroupIds,
            onChange: handleGroupChange,
          }}
          multiSelectField={{
            name: `${name}-group`,
            type: FormFieldType.MultiSelect,
            label: groupLabel,
            options: groupedItems.map(({ id, name }) => ({ id, label: name })),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default LinkedGroupedMultiSelectInput;
